/* ============================================================================
   ASSETWORKBENCH.JSX
   ----------------------------------------------------------------------------
   MOTEUR UTILITAIRE RÉUTILISABLE
   ----------------------------------------------------------------------------
   Ce fichier ne porte AUCUNE UI.
   Il centralise uniquement la logique commune du workbench :

   - duplication
   - suppression
   - protection de suppression
   - mise à jour d’un item
   - mise à jour d’un champ
   - synchronisation summaryValue ← field source

   Pourquoi cette approche ?
   ------------------------
   La page reste propriétaire de l’état :
   - items
   - selectedItemId
   - chargement API
   - sauvegarde .NET / PostgreSQL

   Mais la logique transverse ne doit pas être dupliquée dans plusieurs pages
   ou plusieurs panneaux. Elle vit donc ici.

   Ce fichier est volontairement "headless" :
   - pas de JSX
   - pas de layout
   - pas de dépendance UI

   Il est donc réutilisable pour :
   - Specific Assets
   - Technical Systems
   - futurs modules de librairie
============================================================================ */

/* ============================================================================
   NORMALISATION TITRE DE BASE
   ----------------------------------------------------------------------------
   Retire les suffixes "(copie)" / "(copie n)" pour éviter l’accumulation.
============================================================================ */
export const normalizeWorkbenchTitleBase = (title) => {
  return String(title || "")
    .replace(/\s+\(copie(?:\s+\d+)?\)$/i, "")
    .trim();
};

/* ============================================================================
   CONSTRUCTION DU TITRE DUPLIQUÉ
============================================================================ */
export const buildDuplicatedTitle = (title, duplicateIndex) => {
  const baseTitle = normalizeWorkbenchTitleBase(title);

  if (duplicateIndex <= 1) {
    return `${baseTitle} (copie)`;
  }

  return `${baseTitle} (copie ${duplicateIndex})`;
};

/* ============================================================================
   CONSTRUCTION D’UN ID DUPLIQUÉ UNIQUE
============================================================================ */
export const buildDuplicatedId = (baseId, duplicateIndex, allItems = []) => {
  const normalizedBaseId = String(baseId || "item")
    .replace(/-copy(?:-\d+)?$/i, "")
    .trim();

  let candidate =
    duplicateIndex <= 1
      ? `${normalizedBaseId}-copy`
      : `${normalizedBaseId}-copy-${duplicateIndex}`;

  let sequence = duplicateIndex <= 1 ? 2 : duplicateIndex + 1;

  while (allItems.some((item) => item.id === candidate)) {
    candidate = `${normalizedBaseId}-copy-${sequence}`;
    sequence += 1;
  }

  return candidate;
};

/* ============================================================================
   CALCUL INDEX DE DUPLICATION
============================================================================ */
export const getNextDuplicateIndex = (sourceItem, allItems = []) => {
  const normalizedTitle = normalizeWorkbenchTitleBase(sourceItem?.title);

  const duplicatesCount = allItems.filter((item) => {
    if (item.family !== sourceItem.family) return false;
    return normalizeWorkbenchTitleBase(item.title) === normalizedTitle;
  }).length;

  return duplicatesCount;
};

/* ============================================================================
   CRÉATION D’UN DUPLICATA
============================================================================ */
export const createDuplicatedItem = (sourceItem, allItems = []) => {
  const duplicateIndex = getNextDuplicateIndex(sourceItem, allItems);

  return {
    ...sourceItem,
    id: buildDuplicatedId(sourceItem.id, duplicateIndex, allItems),
    title: buildDuplicatedTitle(sourceItem.title, duplicateIndex),
    fields: {
      ...(sourceItem.fields || {}),
    },
  };
};

/* ============================================================================
   DUPLICATION DANS UNE COLLECTION
   ----------------------------------------------------------------------------
   Retourne :
   - nextItems
   - duplicatedItem
============================================================================ */
export const duplicateWorkbenchItem = (items = [], itemId) => {
  const sourceItem = items.find((item) => item.id === itemId);
  if (!sourceItem) {
    return {
      nextItems: items,
      duplicatedItem: null,
    };
  }

  const duplicatedItem = createDuplicatedItem(sourceItem, items);

  return {
    nextItems: [...items, duplicatedItem],
    duplicatedItem,
  };
};

/* ============================================================================
   ÉTAT DE PROTECTION DE SUPPRESSION
   ----------------------------------------------------------------------------
   Règle métier :
   Suppression interdite si l’élément sélectionné est le dernier actif
   de sa famille.
============================================================================ */
export const getDeleteProtectionState = (items = [], itemId) => {
  const targetItem = items.find((item) => item.id === itemId);

  if (!targetItem) {
    return {
      canDelete: false,
      reason: "",
    };
  }

  const activeInFamily = items.filter(
    (item) => item.family === targetItem.family && item.active
  ).length;

  const isLastActiveOfFamily = targetItem.active && activeInFamily <= 1;

  return {
    canDelete: !isLastActiveOfFamily,
    reason: isLastActiveOfFamily
      ? "Suppression désactivée : dernier actif de cette catégorie."
      : "",
  };
};

/* ============================================================================
   SUPPRESSION DANS UNE COLLECTION
   ----------------------------------------------------------------------------
   Retourne :
   - nextItems
   - nextSelectedItemId
   - deleted
   - reason
============================================================================ */
export const deleteWorkbenchItem = (
  items = [],
  itemId,
  selectedItemId = null
) => {
  const protection = getDeleteProtectionState(items, itemId);

  if (!protection.canDelete) {
    return {
      nextItems: items,
      nextSelectedItemId: selectedItemId,
      deleted: false,
      reason: protection.reason,
    };
  }

  const nextItems = items.filter((item) => item.id !== itemId);

  const nextSelectedItemId =
    selectedItemId === itemId ? nextItems[0]?.id ?? null : selectedItemId;

  return {
    nextItems,
    nextSelectedItemId,
    deleted: true,
    reason: "",
  };
};

/* ============================================================================
   MISE À JOUR GÉNÉRIQUE D’UN ITEM
============================================================================ */
export const updateWorkbenchItem = (items = [], itemId, updater) => {
  return items.map((item) => (item.id === itemId ? updater(item) : item));
};

/* ============================================================================
   MISE À JOUR D’UN CHAMP D’UN ITEM
   ----------------------------------------------------------------------------
   Synchronise summaryValue si le field modifié est déclaré comme source
   de synthèse dans fieldSchema[family].summarySourceField
============================================================================ */
export const updateWorkbenchItemField = ({
  items = [],
  itemId,
  fieldName,
  value,
  fieldSchema = {},
}) => {
  return updateWorkbenchItem(items, itemId, (item) => {
    const nextFields = {
      ...(item.fields || {}),
      [fieldName]: value,
    };

    const familyConfig = fieldSchema[item.family] || {};
    const summaryField = familyConfig.summarySourceField;

    let nextSummaryValue = item.summaryValue;

    if (summaryField && fieldName === summaryField) {
      nextSummaryValue = value === "" ? item.summaryValue : value;
    }

    return {
      ...item,
      fields: nextFields,
      summaryValue: nextSummaryValue,
    };
  });
};